import { useState } from "react";
import styles from "./Reviews.module.css";
import { Card, IconButton, Stack, Typography } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { reviews } from "./clientReviews";
import { trackCarouselInteraction } from "../../utils/tracking";
import useDocumentMeta from "../../utils/useDocumentMeta";

  export const renderStars = (rating) => {
    const numericRating = Math.max(0, Math.min(5, Number(rating) || 0));
    return Array.from({ length: 5 }, (_, index) => {
      const fillPercent = Math.round(
        Math.max(0, Math.min(1, numericRating - index)) * 100,
      );
      return (
        <span key={index} className={styles.star}>
          ★
          <span
            className={styles.starFill}
            style={{ width: `${fillPercent}%` }}
          >
            ★
          </span>
        </span>
      );
    });
  };

const Reviews = () => {
  useDocumentMeta(
    "Client Reviews | Radiance Aesthetics | Hindley | Wigan",
    "Read real client reviews for Radiance Aesthetics, a nurse-led aesthetics clinic in Hindley, Wigan offering facials, microneedling and skin treatments.",
  );

  const [currentIndex, setCurrentIndex] = useState(0);


  const goToPrevious = () => {
    trackCarouselInteraction("Reviews Page", "prev");
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? reviews.length - 1 : prevIndex - 1,
    );
  };

  const goToNext = () => {
    trackCarouselInteraction("Reviews Page", "next");
    setCurrentIndex((prevIndex) =>
      prevIndex === reviews.length - 1 ? 0 : prevIndex + 1,
    );
  };

  return (
    <section className={styles.reviewsColourSection}>
      <Stack
        sx={{
          maxWidth: "1108px",
          margin: "0 auto",
          alignItems: "center",
          padding: { xs: "20px 16px", md: "20px 24px" },
        }}
      >
        <Typography
          sx={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            color: "var(--color-accent)",
            textAlign: "center",
            mb: 1.5,
          }}
        >
          Testimonials
        </Typography>
        <Typography
          sx={{
            fontFamily: "var(--font-heading)",
            color: "var(--color-primary)",
            fontWeight: 400,
            textAlign: "center",
            fontSize: { xs: "28px", md: "40px" },
            mb: 1.5,
          }}
          variant="h1"
        >
          What Our Clients Say
        </Typography>

        <Typography
          sx={{
            color: "var(--color-text-light)",
            textAlign: "center",
            fontSize: "16px",
            mb: 5,
            maxWidth: "600px",
            lineHeight: 1.7,
            fontFamily: "var(--font-body)",
          }}
        >
          Read what our valued clients have to say about their experience with
          Radiance Aesthetics
        </Typography>

        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          gap="20px"
          position="relative"
        >
          <IconButton
            size="large"
            sx={{
              backgroundColor: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              "&:hover": { backgroundColor: "#f9f9f9" },
              display: { xs: "none", sm: "flex" },
            }}
            onClick={goToPrevious}
            aria-label="Previous review"
          >
            <KeyboardArrowLeftIcon />
          </IconButton>

          <Card
            sx={{
              width: { xs: "100%", sm: "500px", md: "600px" },
              maxWidth: "600px",
              overflow: "hidden",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(90, 77, 71, 0.08)",
              backgroundColor: "white",
              border: "1px solid var(--color-border)",
            }}
          >
            <Stack
              sx={{
                padding: { xs: "24px", sm: "32px", md: "40px" },
                minHeight: "280px",
                justifyContent: "center",
              }}
            >
              <Stack sx={{ flexDirection: "row", gap: "4px", mb: 2 }}>
                {renderStars(reviews[currentIndex].rating)}
              </Stack>
              <Typography
                sx={{
                  fontFamily: "var(--font-heading)",
                  fontSize: { xs: "17px", md: "20px" },
                  lineHeight: 1.6,
                  color: "var(--color-text)",
                  fontStyle: "italic",
                  mb: 3,
                }}
              >
                "{reviews[currentIndex].text}"
              </Typography>
              <Typography
                sx={{
                  fontFamily: "var(--font-body)",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "var(--color-primary)",
                }}
              >
                {reviews[currentIndex].name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "var(--font-body)",
                  fontSize: "13px",
                  color: "var(--color-text-muted)",
                  mt: 0.5,
                }}
              >
                {reviews[currentIndex].service}
              </Typography>
            </Stack>
          </Card>

          <IconButton
            size="large"
            sx={{
              backgroundColor: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              "&:hover": { backgroundColor: "#f9f9f9" },
              display: { xs: "none", sm: "flex" },
            }}
            onClick={goToNext}
            aria-label="Next review"
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Stack>

        <Stack
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          gap={2}
          sx={{ mt: 3, display: { xs: "flex", sm: "none" } }}
        >
          <IconButton
            onClick={goToPrevious}
            aria-label="Previous review"
            sx={{
              backgroundColor: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <IconButton
            onClick={goToNext}
            aria-label="Next review"
            sx={{
              backgroundColor: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Stack>

        <div className={styles.reviewIndicators}>
          {reviews.map((_, index) => (
            <button
              key={index}
              className={`${styles.indicator} ${
                index === currentIndex ? styles.active : ""
              }`}
              onClick={() => {
                trackCarouselInteraction("Reviews Page", "dot", index);
                setCurrentIndex(index);
              }}
              aria-label={`Go to review ${index + 1}`}
            />
          ))}
        </div>
      </Stack>
    </section>
  );
};

export default Reviews;
